import anime from 'animejs/lib/anime.es.js';

import {countDecimals, interpolateNumber} from './utils.js';

/**
 * Animates the graph data when in view.
 *
 * data-graph-overall
 * data-graph-breakdown
 */

// console.log('Site:graph');

const DELAY_VALUE_INCREMENT = 15;
const DELAY_ROW_INCREMENT = 5;


export class GraphOverall {
  element;
  goalOverall;

  constructor(element) {
    // console.log('GraphOverall:constructor', element);

    this.element = element;
    this.goalOverall    = element.querySelector('.overall-goal');

    const options = {
      // rootMargin : '0px',
      threshold : 0.5,
    };

    this.observer = new IntersectionObserver(this.intersectionCallback.bind(this), options);

    this.observer.observe(element);
  }


  intersectionCallback(entries) {
    // console.log('GraphOverall:intersectionCallback');

    if (entries[0].intersectionRatio > 0) {
      // console.log('Visible');
      this.observer.disconnect();
      this.animate();
    }
  }


  animate() {
    // console.log('GraphOverall:animate');
    // console.log('this.element', this.element);

    let delay = 0;

    if (this.goalOverall) {
      const goalOverallLabel = this.goalOverall.querySelector('.value');
      const goalOverallValue = parseFloat(this.goalOverall.dataset.goal);
      const decimals = countDecimals(goalOverallValue) || 1;

      anime({
        targets  : this.goalOverall,
        left     : goalOverallValue + '%',
        opacity  : 1,
        duration : 750,
        easing   : 'easeOutQuad',
        update   : (anim) => {
          const progress = (anim.progress / 100 * goalOverallValue).toFixed(decimals);

          goalOverallLabel.innerText = progress + '%';
        },
        complete : () => {
          this.goalOverall.classList.add('animation-done');
          goalOverallLabel.innerText = goalOverallValue + '%';
        },
      });
    }

    setTimeout(() => {
      this.element.querySelectorAll('.domain').forEach((element) => {
        // console.log('element', element);

        let data = element.dataset.data;

        try {
          data = JSON.parse(data);
          data = data[Object.keys(data)[0]];
        } catch (e) {
          console.log('Bad graph data', e, element);

          return;
        }

        // console.log('data', data);

        const isGoalValueType = element.classList.contains('type-goal-actual');
        const goalElement = element.querySelector('.goal');

        if (goalElement) {
          delay += DELAY_VALUE_INCREMENT;

          if (data.goal_value) {
            this.animateIn(goalElement, data.goal_value, delay, isGoalValueType);
          } else {
            goalElement.classList.add('no-value');

            if ( data.year == 2020 ) {
              goalElement.querySelector('.label').innerText = 'Not Tested*';
            } else {
              goalElement.querySelector('.label').innerText = '';
            }
          }
        }

        const actualElement = element.querySelector(isGoalValueType ? '.actual' : '.engaged-container');

        if (actualElement) {
          delay += DELAY_VALUE_INCREMENT;

          if (data.actual_value) {
            this.animateIn(actualElement, data.actual_value, delay, isGoalValueType);
          } else {
            actualElement.classList.add('no-value');

            if (data.year == 2020 && !data.hide_not_tested) {
              actualElement.querySelector('.label').innerText = 'Not Tested*';
            } else {
              actualElement.querySelector('.label').innerText = '';
            }
          }
        }

        delay += DELAY_ROW_INCREMENT;
      });
    }, 600);
  }


  animateIn(target, width, delay, isGoalValueType) {
    // console.log(target, 'isGoalValueType', isGoalValueType);

    let goalLabel;
    let unengagedLabel;
    let animationTarget;

    if (isGoalValueType) {
      animationTarget = target;
      goalLabel = target.querySelector('.label .value');
    } else {
      animationTarget = target.querySelector('.engaged');
      goalLabel = target.querySelector('.engaged .value');
      unengagedLabel = target.querySelector('.unengaged .value');
    }

    const decimals = countDecimals(width) || 1;

    anime({
      targets  : animationTarget,
      width,
      delay,
      duration : 750,
      easing   : 'easeOutQuad',
      update   : (anim) => {
        const progress = (anim.progress / 100 * width).toFixed(decimals);

        goalLabel.innerText = progress + '%';

        if (!isGoalValueType) {
          unengagedLabel.innerText = (100 - progress).toFixed(decimals) + '%';
        }
      },
      complete : () => {
        goalLabel.innerText = width + '%';
      },
    });
  }
}


export class GraphBreakdown {
  element;
  graph;
  graphContainer;
  button;
  years;
  animations = new WeakMap();


  constructor(element) {
    // console.log('GraphBreakdown:constructor', element);

    this.element        = element;
    this.graph          = element.querySelector('.graph');
    this.graphContainer = element.querySelector('.breakdown');
    this.button         = element.querySelector('button.demographic-breakdown');
    this.years          = this.element.querySelector('.years');

    if (this.years) {
      this.years.addEventListener('click', (event) => this.animate(event.target.dataset.year));
    }

    if (this.button) {
      this.button.addEventListener('click', () => this.toggleVisibility());
    }
  }


  toggleVisibility() {
    // console.debug('GraphBreakdown:toggleVisibility');

    if (this.graphContainer.hasAttribute('hidden')) {
      this.graphContainer.removeAttribute('hidden');
      this.button.setAttribute('aria-expanded', true);
      this.button.textContent = 'Close';

      this.animateRecentYear();
    } else {
      this.graphContainer.setAttribute('hidden', '');
      this.button.setAttribute('aria-expanded', false);
      this.button.textContent = 'Demographic Breakdown';
    }
  }


  animateRecentYear() {
    // console.log('GraphBreakdown:animateRecentYear');

    this.animate(this.getRecentYear());
  }


  getRecentYear() {
    // console.log('GraphBreakdown:getRecentYear');

    try {
      const data = JSON.parse(this.graph.querySelector('.domain').dataset.data);
      const keys = Object.keys(data);

      return keys.slice(-1)[0];
    } catch (e) {
      console.log('Bad graph data', e, this.graph);
    }

    return 0;
  }


  animate(year) {
    // console.log('GraphBreakdown:animate', year);

    let delay = 0;

    this.years.querySelectorAll('button').forEach((element) => {
      if (element.dataset.year == year) {
        element.classList.add('active');
      } else {
        element.classList.remove('active');
      }
    });

    this.element.querySelectorAll('.domain').forEach((element) => {
      // console.log('element', element);

      let data = element.dataset.data;

      // console.log('element', element, data);

      try {
        data = JSON.parse(data);
        data = data[year];
      } catch (e) {
        console.log('Bad graph data', e, element);

        return;
      }

      console.log('data', data);

      if (data) {

        const goalElement = element.querySelector('.goal');

        if (goalElement) {
          delay += DELAY_VALUE_INCREMENT;

          this.animateIn(goalElement, data.goal_value, delay, year);
        }

        const actualElement = element.querySelector('.actual');

        if (actualElement) {
          delay += DELAY_VALUE_INCREMENT;

          this.animateIn(actualElement, data.actual_value, delay, year);
        }

        delay += DELAY_ROW_INCREMENT;
      }
    });
  }


  animateIn(target, width, delay, year) {
    const goalLabel = target.querySelector('.label');
    const decimals = countDecimals(width) || 1;

    let startValue = 0;

    width = Number(width);

    if (typeof target.dataset.target === 'undefined') {
      target.dataset.target = -1;
    } else {
      startValue = Number(target.dataset.target);
    }

    // console.log(target, startValue, target.dataset.target, width);

    if (width === Number(target.dataset.target)) {
      // console.log('Value the same');
      return;
    }

    if (width) {
      target.classList.remove('no-value');
    }

    target.dataset.target = width;

    // console.log('target', target, this.animations.has(target));

    if (this.animations.has(target)) {
      // console.log('Exists', this.animations.get(target));
      this.animations.get(target).pause();
    }

    const animejs = anime({
      targets  : target,
      width,
      delay,
      duration : 750,
      easing   : 'easeOutQuad',
      update   : (anim) => {
        goalLabel.innerText = interpolateNumber(startValue, width, anim.progress / 100).toFixed(decimals) + '%';
      },
      complete : () => {
        if (!width) {
          target.classList.add('no-value');
          if ( year == 2020 ) {
            goalLabel.innerText = 'Not Tested';
          } else {
            goalLabel.innerText = '';
          }
        } else {
          goalLabel.innerText = width + '%';
        }
      },
    });

    this.animations.set(target, animejs);
  }
}
